import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  GetArg,
  PutArg,
  PostArg,
  DeleteArg,
  PostBlockArg,
  CustomErrorResponse,
  PostExFMError,
} from './apiTypes';
import { VERSION, ERROR_CODE } from '../_constants/apiConst';
import { isArray } from 'lodash';
import { CustomError } from './CustomError';
import { ExFMError } from './ExFMError';

/**
 * APIのURL取得
 * @param endpoint
 * @returns
 */
export function getApiUrl(endpoint: string) {
  return `${process.env.REACT_APP_BACKEND_URL}/${VERSION}/${endpoint}`;
}

/** API用のリクエストヘッダ作成(認証のためにcredentialsをつけて返却) */
export function createHeaderWithCredentials(
  arg: GetArg | PutArg | PostArg | DeleteArg | PostBlockArg,
): AxiosRequestConfig {
  const config: AxiosRequestConfig = {
    method: arg.method,
    withCredentials: true,
    timeout: 100000,
  };

  if (isPutOrPost(arg) || isPostBlock(arg)) {
    if (arg.body && arg.contentType) {
      config.headers = { 'Content-Type': arg.contentType };
      config.data = arg.body;
    }
  }

  return config;
}

const isPutOrPost = (arg: any): arg is PutArg | PostArg => {
  return (
    arg &&
    arg.body &&
    arg.contentType &&
    typeof arg.method === 'string' &&
    typeof arg.body === 'string' &&
    typeof arg.contentType === 'string'
  );
};

const isPostBlock = (arg: any): arg is PostBlockArg => {
  return (
    arg &&
    arg.body &&
    arg.contentType &&
    typeof arg.method === 'string' &&
    typeof arg.contentType === 'string'
  );
};

export const getHeader = (
  arg: GetArg | PutArg | PostArg | DeleteArg | PostBlockArg,
) => {
  return arg;
};

/** APIのリクエストが成功したか(200系か) */
export function isRequestSuccessful(response: unknown) {
  if (typeof response === 'number') {
    return response >= 200 && response < 300;
  }

  if (typeof response === 'string') {
    return response === 'fulfilled';
  }
  return false;
}

/** BE側のロジックで返すエラーレスポンスかどうか */
export function isCustomErrorResponse(data: any): data is CustomErrorResponse {
  return (
    data &&
    typeof data.errorCode === 'string' &&
    isArray(data.targetData) &&
    data.targetData.every((e: any) => e && typeof e === 'string')
  );
}

/** ExFMから返されたエラーレスポンスかどうか */
export function isPostExFMError(data: any): data is PostExFMError {
  return (
    data &&
    typeof data.errorMessage === 'string' &&
    (typeof data.errorCode === 'string' || typeof data.errorCode === 'number')
  );
}

export const fetchWithAuth = async (
  url: string,
  config: AxiosRequestConfig,
): Promise<AxiosResponse> => {
  try {
    const response = await axios(url, config);
    if (!isRequestSuccessful(response.status)) {
      throw new Error(String(response.status));
    }
    return response;
  } catch (error) {
    if (!axios.isAxiosError(error)) {
      throw new Error(error instanceof Error ? error.message : '0');
    }

    // タイムアウトのハンドリング
    if (error.code === 'ECONNABORTED') {
      throw new Error(String(ERROR_CODE.TIMEOUT));
    }

    const status = error.status;

    // BE側のロジックで返すエラーはCustomErrorクラスでthrowする
    if (error.response && isCustomErrorResponse(error.response.data)) {
      const { errorCode, targetData } = error.response.data;
      throw new CustomError(String(status), errorCode, targetData);
    }

    // ExFMから返されたエラーはExFmErrorクラスでthrowする
    if (error.response && isPostExFMError(error.response.data)) {
      const { errorMessage, errorCode } = error.response.data;
      throw new ExFMError(String(status), errorMessage, errorCode.toString());
    }

    throw new Error(String(status));
  }
};
